'use client'

/**
 * FRIDGE-760 — the ONE retailer buy button on the product page, and the
 * "Learn about this recall" link that takes its prominence when a recall is
 * active. Every CTA site (offer list, mobile sticky bar, desktop sticky bar)
 * renders through here so the tone rule in lib/product/recall-cta.ts cannot
 * be bypassed by a hard-coded class; tests/recall-cta-deemphasis-fridge-760
 * renders both states and pins the sites.
 *
 * The button stays a SignedOfferLink — same signed /r/ redirect, same
 * attribution — only its tone changes. It is never hidden.
 */
import { SignedOfferLink, type SignedOfferLinkProps } from '@/components/product/signed-offer-link'
import { offerCtaTone, PRIMARY_OFFER_CTA, RECALL_BANNER_ID } from '@/lib/product/recall-cta'

type Size = 'xs' | 'sm'
const DIMS: Record<Size, string> = { xs: 'px-5 py-1.5 text-xs', sm: 'px-6 py-2.5 text-sm' }
const SHAPE = 'rounded-lg font-bold transition-all shadow-sm active:scale-95 flex-shrink-0'

export function RetailerOfferCta({
  hasActiveRecall,
  size = 'xs',
  className = '',
  children,
  ...rest
}: SignedOfferLinkProps & { hasActiveRecall: boolean; size?: Size }) {
  return (
    <SignedOfferLink
      {...rest}
      data-recall-deemphasized={hasActiveRecall ? 'true' : undefined}
      className={`${offerCtaTone(hasActiveRecall)} ${DIMS[size]} ${SHAPE} ${className}`.trim()}
    >
      {children}
    </SignedOfferLink>
  )
}

export function LearnAboutRecallLink({ size = 'xs', className = '' }: { size?: Size; className?: string }) {
  return (
    <a
      href={`#${RECALL_BANNER_ID}`}
      data-testid="learn-about-recall"
      className={`${PRIMARY_OFFER_CTA} ${DIMS[size]} ${SHAPE} inline-flex items-center whitespace-nowrap ${className}`.trim()}
      onClick={(e) => {
        const el = typeof document !== 'undefined' ? document.getElementById(RECALL_BANNER_ID) : null
        if (!el) return
        e.preventDefault()
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }}
    >
      Learn about this recall
    </a>
  )
}
